import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import { AppFilterBar, AppFilterSelect } from '../../components/filters';
import secteursService from '../../services/secteursService';
import DashboardV2GlobalCards from '../../components/secteurs/dashboard/DashboardV2GlobalCards';
import DashboardV2VendeurChips from '../../components/secteurs/dashboard/DashboardV2VendeurChips';
import DashboardV2StatutsRows from '../../components/secteurs/dashboard/DashboardV2StatutsRows';
import DashboardV2SigneRows from '../../components/secteurs/dashboard/DashboardV2SigneRows';
import DashboardV2PipeRows from '../../components/secteurs/dashboard/DashboardV2PipeRows';
import { PIPE_SOURCE_LABELS } from '../../utils/dashboardV2Utils';
import { buildCommercialColorByName } from '../../utils/commercialBadgeUtils';
import { AppAlert, AppSpinner } from '../../components/ui';
import '../../styles/secteurs-dashboard-v2.scss';
import '../../styles/kkp-planning-page.scss';

const EMPTY_DATA = {
  cards_globales: {
    entreprises_locales: { total: 0, label: 'Entreprises SIRENE (actives)' },
    entreprises_hubspot: { total: 0, pourcentage: 0, label: 'Entreprises Hubspot' },
    dolibarr_clients: { total: 0, pourcentage: 0, label: 'Comptes clients Dolibarr' },
  },
  statuts_rows: [],
  signe_rows: [],
  pipe_rows: [],
};

const SecteursDashboardPage = () => {
  const [meta, setMeta] = useState({ annee: new Date().getFullYear(), commerciaux: [], secteurs: [] });
  const [data, setData] = useState(EMPTY_DATA);
  const [secteur, setSecteur] = useState([]);
  const [pipeSource, setPipeSource] = useState(['both']);
  const [vendeurs, setVendeurs] = useState(['__TOUS__']);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const colorByName = useMemo(
    () => buildCommercialColorByName(meta.commerciaux),
    [meta.commerciaux],
  );

  const secteurApi = secteur[0] || '__TOUS__';
  const pipeSourceApi = pipeSource[0] || 'both';

  const secteurOptions = useMemo(
    () => meta.secteurs.map((s) => ({
      value: String(s.id),
      label: `${s.nomCommercial}${s.nomSecteur ? ` — ${s.nomSecteur}` : ''}`,
    })),
    [meta.secteurs],
  );

  const pipeSourceOptions = useMemo(() => [
    { value: 'both', label: 'Commandes + Factures' },
    { value: 'commandes', label: 'Commandes uniquement' },
    { value: 'factures', label: 'Factures uniquement' },
  ], []);

  const fetchStats = useCallback(async (showLoader = true) => {
    if (showLoader) setUpdating(true);
    setError(null);
    try {
      const stats = await secteursService.getDashboardV2Stats({
        vendeurs,
        secteur: secteurApi,
        pipeSource: pipeSourceApi,
      });
      setData(stats || EMPTY_DATA);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des statistiques');
    } finally {
      setUpdating(false);
      setLoading(false);
    }
  }, [vendeurs, secteurApi, pipeSourceApi]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const metaRes = await secteursService.getDashboardV2Meta();
        if (!cancelled) setMeta(metaRes);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    };

    init();
    fetchStats();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loading) return undefined;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchStats(false), 200);
    return () => clearTimeout(debounceRef.current);
  }, [vendeurs, secteurApi, pipeSourceApi, fetchStats, loading]);

  return (
    <MasterLayout>
      <div className="secteurs-dashboard-v2">
        <Breadcrumb
          title="Dashboard Secteurs"
          subtitle={`Année ${meta.annee} — filtres dynamiques`}
        />

        {updating && (
          <div className="v2-loader badge bg-primary-600 p-2">
            <AppSpinner size="sm" centered={false} variant="light" className="me-1" />
            Mise à jour…
          </div>
        )}

        {error && <AppAlert>{error}</AppAlert>}

        <AppFilterBar showReset={false}>
          <AppFilterSelect
            id="dashboard-secteur"
            label="Secteur géographique"
            col="col-md-6"
            value={secteur}
            onChange={(next) => setSecteur(next.slice(-1))}
            options={secteurOptions}
            multiple={false}
            placeholder="Tous les secteurs"
          />
        </AppFilterBar>

        {loading ? (
          <AppSpinner />
        ) : (
          <>
            <DashboardV2GlobalCards cards={data.cards_globales} />

            <AppFilterBar showReset={false} className="is-sticky">
              <AppFilterSelect
                id="dashboard-pipe-source"
                label="Source CA (section pipe)"
                col="col-md-3"
                value={pipeSource}
                onChange={(next) => setPipeSource(next.slice(-1).length ? next.slice(-1) : ['both'])}
                options={pipeSourceOptions}
                multiple={false}
                searchable={false}
              />
              <div className="col-md-9">
                <div className="form-label">Commerciaux</div>
                <DashboardV2VendeurChips
                  commerciaux={meta.commerciaux}
                  selected={vendeurs}
                  onChange={setVendeurs}
                />
              </div>
            </AppFilterBar>

            <div className="v2-section-card">
              <div
                className="v2-section-card-header"
                style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}
              >
                Répartition par statut — par commercial
              </div>
              <div className="v2-section-card-body">
                <DashboardV2StatutsRows rows={data.statuts_rows} colorByName={colorByName} />
              </div>
            </div>

            <div className="v2-section-card">
              <div
                className="v2-section-card-header"
                style={{ background: 'linear-gradient(135deg, #059669, #10b981)' }}
              >
                Signé — Fidélisé &amp; Nouveau client
              </div>
              <div className="v2-section-card-body">
                <DashboardV2SigneRows rows={data.signe_rows} colorByName={colorByName} />
              </div>
            </div>

            <div className="v2-section-card">
              <div
                className="v2-section-card-header"
                style={{ background: 'linear-gradient(135deg, #b45309, #f59e0b)' }}
              >
                Clients par pipe
                <span className="badge bg-light text-dark ms-2">
                  {PIPE_SOURCE_LABELS[pipeSourceApi]}
                </span>
              </div>
              <div className="v2-section-card-body">
                <DashboardV2PipeRows rows={data.pipe_rows} colorByName={colorByName} />
              </div>
            </div>
          </>
        )}
      </div>
    </MasterLayout>
  );
};

export default SecteursDashboardPage;
